import React from 'react';

import createTextMaskInputElement from './create-text-mask-input-element';
import { MaskInputProps, SettingsPropsKeys } from './types';
import { isNil } from './utilities';

export class MaskedInput extends React.PureComponent<MaskInputProps> {
  declare props: MaskInputProps;
  textMaskInputElement: any;
  inputElement: any;
  static defaultProps: MaskInputProps;

  constructor(args: MaskInputProps) {
    super(args);
    this.setRef = this.setRef.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onTextChange = this.onTextChange.bind(this);
  }

  onTextChange(text: any) {
    this.props.onTextChange?.(text);
  }

  setRef(inputElement: HTMLInputElement) {
    this.inputElement = inputElement;
  }

  initTextMask() {
    const {
      props,
      props: { value },
    } = this;

    this.textMaskInputElement = createTextMaskInputElement({
      inputElement: this.inputElement,
      ...props,
      currentCaretPosition: 0,
    });
    this.textMaskInputElement.update(value);
  }

  componentDidMount() {
    this.initTextMask();
  }

  componentDidUpdate(prevProps: any) {
    const { value, pipe, mask, guide, placeholderChar, showMask } = this.props;

    const settings = { guide, placeholderChar, showMask };
    const isPipeChanged =
      typeof pipe === 'function' && typeof prevProps.pipe === 'function'
        ? pipe.toString() !== prevProps.pipe.toString()
        : (isNil(pipe) && !isNil(prevProps.pipe)) || (!isNil(pipe) && isNil(prevProps.pipe));
    const isMaskChanged = mask?.toString() !== prevProps.mask.toString();
    const isSettingChanged =
      Object.keys(settings).some(
        (prop) => settings[prop as SettingsPropsKeys] !== prevProps[prop as SettingsPropsKeys],
      ) ||
      isMaskChanged ||
      isPipeChanged;

    const isValueChanged = value !== undefined && value !== this.inputElement.value;

    if (isValueChanged || isSettingChanged) {
      this.initTextMask();
      this.onTextChange(this.inputElement.value);
    }
  }

  render() {
    const { render, ...props } = this.props;

    delete props.mask;
    delete props.guide;
    delete props.pipe;
    delete props.placeholderChar;
    delete props.keepCharPositions;
    delete props.value;
    delete props.onBlur;
    delete props.onChange;
    delete props.onTextChange;
    delete props.showMask;

    return render(this.setRef, {
      onBlur: this.onBlur,
      onChange: this.onChange,
      onTextChange: this.onTextChange,
      defaultValue: this.props.value,
      ...props,
    });
  }

  onChange(event: any) {
    this.textMaskInputElement.update();

    if (typeof this.props.onChange === 'function') {
      this.props.onChange(event);
    }
    this.onTextChange?.(event.target.value);
  }

  onBlur(event: any) {
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(event);
    }
  }
}

MaskedInput.defaultProps = {
  render: (
    ref: React.LegacyRef<HTMLInputElement>,
    {
      onTextChange,
      ...props
    }: JSX.IntrinsicAttributes & { onTextChange: MaskInputProps['onTextChange'] },
  ) => <input ref={ref} {...props} />,
};
